/**
 * WebchatWidget — floating chat widget rendered inside the Santinho Digital.
 *
 * Two-phase UX: (1) form capturing name/phone + Turnstile + consent, and
 * (2) conversation pane once the session is opened. Real-time delivery of
 * operator messages uses the tenant WebSocket with a visitor-scoped JWT;
 * REST polling of `listMessages` guards against reconnection gaps.
 *
 * The widget is mounted by each santinho template when
 * `tenant.publicPage.webchat?.enabled === true`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { TenantPublicConfig } from '@/types/tenant.types';
import { isValidPhone } from '@/types/contact.types';
import { Turnstile } from '@/components/Turnstile';
import { useWebchatSession } from '@/hooks/useWebchatSession';
import { useWebchatWebSocket } from '@/hooks/useWebchatWebSocket';
import { webchatService, WebchatMessageItem } from '@/services/webchat.service';
import { renderWhatsAppMarkup } from '@/components/webchat/whatsappFormat';

interface WebchatWidgetProps {
  tenant: TenantPublicConfig;
}

type Phase = 'closed' | 'form' | 'chat';

const POLL_INTERVAL_MS = 5000;
const DEFAULT_HEADER_TITLE = 'Converse conosco';
const DEFAULT_HEADER_SUBTITLE = 'Respondemos o mais rápido possível';
const DEFAULT_WELCOME = 'Olá! Como podemos te ajudar hoje?';

export function WebchatWidget({ tenant }: WebchatWidgetProps) {
  const config = tenant.publicPage.webchat;
  const primary = tenant.theme.primaryColor || '#1d4ed8';

  const [phase, setPhase] = useState<Phase>('closed');
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<WebchatMessageItem[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const { session, start, clear } = useWebchatSession(tenant.slug);
  const lastSeenRef = useRef<string | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // If we rehydrated a live session, jump straight to the chat pane.
  useEffect(() => {
    if (session?.token) {
      setPhase((current) => (current === 'closed' ? 'closed' : 'chat'));
    }
  }, [session?.token]);

  const pushMessage = useCallback((msg: WebchatMessageItem) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      const next = [...prev, msg].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      // Only advance the polling cursor using server-confirmed messages.
      // Optimistic echoes carry the phone's clock (`new Date().toISOString()`)
      // and a mobile clock that is a few seconds ahead would otherwise push
      // `lastSeenRef` past the server-side `created_at` of the AI reply, making
      // the polling filter `m.created_at > since` silently drop it.
      const latestServerMsg = [...next]
        .reverse()
        .find((m) => !m.id.startsWith('local-'));
      if (latestServerMsg) {
        lastSeenRef.current = latestServerMsg.createdAt;
      }
      return next;
    });
  }, []);

  useWebchatWebSocket({
    token: phase === 'chat' ? session?.token ?? null : null,
    conversationId: phase === 'chat' ? session?.conversationId ?? null : null,
    onMessage: pushMessage,
  });

  // Polling fallback — safe to run alongside WS, it dedupes by message id.
  useEffect(() => {
    if (phase !== 'chat' || !session?.token) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { messages: fresh } = await webchatService.listMessages(
          session.token,
          lastSeenRef.current
        );
        for (const m of fresh) pushMessage(m);
      } catch {
        // transient — try again on next tick
      }
    };

    void poll();
    const handle = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [phase, session?.token, pushMessage]);

  // Auto-scroll to newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, phase]);

  const openWidget = useCallback(() => {
    setPhase(session?.token ? 'chat' : 'form');
  }, [session?.token]);

  const closeWidget = useCallback(() => {
    setPhase('closed');
  }, []);

  const submitForm = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome é obrigatório');
        return;
      }
      if (!isValidPhone(formPhone)) {
        setFormError('Telefone inválido. Use DDD + número, ex.: 11999999999.');
        return;
      }

      setIsSubmitting(true);
      try {
        await start(tenant.slug, {
          name: formName.trim(),
          phone: formPhone.trim(),
          consent: true,
          consentVersion: 'v1',
          turnstileToken: turnstileToken ?? undefined,
        });
        setPhase('chat');
        setMessages([]);
        lastSeenRef.current = undefined;
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Não foi possível iniciar a conversa');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formName, formPhone, turnstileToken, tenant.slug, start]
  );

  const sendDraft = useCallback(async () => {
    if (!session?.token) return;
    const text = draft.trim();
    if (!text) return;

    setSending(true);
    setChatError(null);
    const localId = `local-${Date.now()}`;
    // Optimistic echo first so the input feels snappy. We reconcile with the
    // server-issued messageId after the POST returns to keep the polling
    // dedup happy (id equality, not content equality).
    pushMessage({
      id: localId,
      direction: 'inbound',
      text,
      senderName: formName || 'Você',
      createdAt: new Date().toISOString(),
    });
    setDraft('');
    try {
      const response = await webchatService.sendMessage(session.token, text);
      setMessages((prev) =>
        prev.map((m) => (m.id === localId ? { ...m, id: response.messageId } : m))
      );
    } catch (err) {
      // Remove the optimistic echo so the user can retry without a ghost bubble.
      setMessages((prev) => prev.filter((m) => m.id !== localId));
      setChatError(err instanceof Error ? err.message : 'Erro ao enviar');
    } finally {
      setSending(false);
    }
  }, [draft, session?.token, pushMessage, formName]);

  if (!config?.enabled) return null;

  const headerTitle = config.headerTitle?.trim() || DEFAULT_HEADER_TITLE;
  const headerSubtitle = config.headerSubtitle?.trim() || DEFAULT_HEADER_SUBTITLE;
  const welcomeMessage = config.welcomeMessage?.trim() || DEFAULT_WELCOME;

  return (
    <>
      <button
        type="button"
        onClick={phase === 'closed' ? openWidget : closeWidget}
        aria-label={phase === 'closed' ? 'Abrir chat' : 'Fechar chat'}
        className="fixed bottom-24 right-6 z-40 h-14 w-14 rounded-full flex items-center justify-center text-white shadow-xl hover:shadow-2xl transition-all"
        style={{ backgroundColor: primary }}
      >
        {phase === 'closed' ? <MessageCircle className="h-6 w-6" /> : <X className="h-6 w-6" />}
      </button>

      {phase !== 'closed' && (
        <div className="fixed bottom-44 right-6 z-40 w-[22rem] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-200">
          <div
            className="px-4 py-3 text-white flex items-center justify-between"
            style={{ backgroundColor: primary }}
          >
            <div className="min-w-0">
              <div className="font-semibold truncate">{headerTitle}</div>
              <div className="text-xs text-white/80 truncate">{headerSubtitle}</div>
            </div>
            <button
              type="button"
              onClick={closeWidget}
              aria-label="Fechar"
              className="text-white/80 hover:text-white leading-none text-2xl"
            >
              ×
            </button>
          </div>

          {phase === 'form' ? (
            <form onSubmit={submitForm} className="p-4 space-y-3">
              <p className="text-sm text-gray-700">{welcomeMessage}</p>

              {formError && (
                <div className="p-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
                  {formError}
                </div>
              )}

              <div>
                <label
                  htmlFor="webchat-name"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Nome *
                </label>
                <input
                  id="webchat-name"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>

              <div>
                <label
                  htmlFor="webchat-phone"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  WhatsApp *
                </label>
                <input
                  id="webchat-phone"
                  type="tel"
                  value={formPhone}
                  placeholder="11999999999"
                  onChange={(e) => setFormPhone(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                />
              </div>

              <div className="flex justify-center">
                <Turnstile
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  size="compact"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                style={{ backgroundColor: primary }}
              >
                {isSubmitting ? 'Iniciando…' : 'Iniciar conversa'}
              </button>

              <p className="text-[10px] text-gray-500 text-center">
                Ao continuar você concorda em receber respostas da campanha.
              </p>
            </form>
          ) : (
            <div className="flex flex-col h-[26rem]">
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50"
              >
                {messages.length === 0 && (
                  <div className="text-xs text-gray-500 text-center py-6">
                    {welcomeMessage}
                  </div>
                )}
                {messages.map((m) => {
                  const fromVisitor = m.direction === 'inbound';
                  return (
                    <div
                      key={m.id}
                      className={`flex ${fromVisitor ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                          fromVisitor
                            ? 'text-white rounded-br-sm'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                        }`}
                        style={fromVisitor ? { backgroundColor: primary } : undefined}
                      >
                        {renderWhatsAppMarkup(m.text ?? '')}
                      </div>
                    </div>
                  );
                })}
              </div>

              {chatError && (
                <div className="px-3 py-1 text-xs text-red-700 bg-red-50 border-t border-red-200">
                  {chatError}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void sendDraft();
                }}
                className="flex items-center gap-2 p-2 border-t border-gray-200 bg-white"
              >
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={sending}
                  placeholder="Digite sua mensagem…"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  aria-label="Enviar"
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white disabled:opacity-50"
                  style={{ backgroundColor: primary }}
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  // Fire-and-forget: server-side close happens inside clear().
                  // We reset the local UI immediately so the visitor doesn't
                  // stare at a disabled widget while the POST is in flight.
                  void clear();
                  setMessages([]);
                  lastSeenRef.current = undefined;
                  setDraft('');
                  setChatError(null);
                  setPhase('closed');
                }}
                className="text-[10px] text-gray-400 hover:text-gray-600 underline py-1"
              >
                Encerrar conversa
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
